import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CoreComponent } from '@components/core.component';
import { VerificationDialog } from '@components/verification-dialog';
import { NgxBarcodeModule } from 'ngx-barcode';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DepositService } from '@app/sharedTemplates/deposit/Deposit.service';
// Routes
import { MobileRouting } from './app.routing';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { RightMenuComponent } from '@components/right-menu.component';
import { LeftMenuComponent } from '@components/left-menu.component';
import { NavbarComponent } from '@components/navbar.component';
import { LocalizationService } from '@services/localization.service';
import { ForgotPasswordComponent } from '@components/forgot-password.component';
import { BottomNavbarComponent } from '@components/bottom-navbar.component';
import { CurrentSlipComponent } from '@components/current-slip.component';
import { InboxDialogComponent } from '@components/inbox.dialog.component';
import { RegisterSuccessComponent } from '@components/register-success.component';
import { DecimalPipe, NgOptimizedImage } from '@angular/common';
import { InputFocusDirective } from './directives/input.focus.directive';
import { BadgeDirective } from './directives/badge.directive';
import { environment, MaterialImports, AritaHammerConfig } from '@environments/environment';
import { RecyleView } from './directives/recyle.view';
import { FooterComponent } from '@components/footer.component';
import { LeftSportMenuDesktop } from '@components/left-sport-menu.desktop';
import { SideBannerSliderComponent } from '@components/slider/sideBanner/side-banner-slider.component';
import { VirtualScrollerModule } from 'ngx-virtual-scroller';
import { DigitOnlyDirective } from './directives/digit.only.directive';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TopLeagues } from '@components/top-leagues.component';
import { QuickTipp } from '@components/quick-tipp';
import { WithdrawResultComponent } from '@components/withdraw-result.component';
import { NgImageSliderModule } from 'ng-image-slider';
import { FileUpload } from '@components/file-upload.element';
import { RECAPTCHA_SETTINGS, RecaptchaFormsModule, RecaptchaModule, RecaptchaSettings } from 'ng-recaptcha';
import { SharedModule } from './modules/shared.module';
import { Ng2FlatpickrModule } from 'ng2-flatpickr';
import { ScannerModule } from '@components/scanner/scanner.module';
import { AccountMenuComponent } from '@components/accountMenu/account-menu.component';
import { LanguageDropdownComponent } from '@components/languageDropdown/language-dropdown.component';
import { UtilsModule } from '@app/modules/utils.module';
import { ConfirmPasswordService, PatternValidatorService } from '@services/custom-validator.service';
import { CacheService } from '@helpers/cache.helper';
import { LiveStreamTvComponent } from '@components/liveStream/live-stream-tv.component';
import { CasinoModule } from '@components/casino/casino.module';
import { QRCodeModule } from 'angularx-qrcode';
import { VerticalSliderComponent } from '@components/slider/verticalSlider/vertical-slider.component';
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { translatumApiUrl } from '@app/constants/projectLangCodeList';
import { appInitializerFactory } from '@app/appInitializerFactory';
import { WithdrawService } from '@sharedTemplates/withdraw/Withdraw.service';
import { CustomFormsModule } from '@app/modules/forms.module';
import { BirthdateBonusComponent } from '@components/bonus/birthdateBonus.component';
import { BetStopReasonModule } from '@components/betStopReason/betStopReason.module';
import { TopWinnerModule } from '@components/topWinner/topWinner.module';
import { CashoutModule } from '@components/cashout/Cashout.module';
import { EventDetailsModule } from '@components/eventDetails/eventDetails.module';
import { LiveEventDetailsModule } from '@components/eventDetails/LiveEventDetails.module';
import { DialogSharedModule } from '@app/dialogs/dialog.module';
import { PaymentResponseErrorSuccessComponent } from '@components/payment-response-error-success';
import { CurrentSlipModule } from '@components/currentSlip/CurrentSlip.module';
import { LoginBaseModule } from '@sharedTemplates/login/login.base.module';
import { SporMobileMenu } from './components/spor-mobile-menu';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, `${translatumApiUrl}${environment.product}/`, '.json');
}

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    return '-';
  }
}

@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialImports,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      isolate: false,
      extend: true,
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler },
      useDefaultLang: true,
    }),
    MobileRouting,
    ScrollingModule,
    VirtualScrollerModule,
    NgxSkeletonLoaderModule,
    NgImageSliderModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    SharedModule,
    UtilsModule,
    CustomFormsModule,
    ScannerModule,
    CasinoModule,
    Ng2FlatpickrModule,
    NgxBarcodeModule,
    AngularSvgIconModule.forRoot(),
    QRCodeModule,
    NgOptimizedImage,
    BetStopReasonModule,
    TopWinnerModule,
    CashoutModule,
    EventDetailsModule,
    LiveEventDetailsModule,
    DialogSharedModule,
    CurrentSlipModule,
    LoginBaseModule,
  ],
  declarations: [
    BirthdateBonusComponent,
    CoreComponent,
    LeftMenuComponent,
    RightMenuComponent,
    NavbarComponent,
    BottomNavbarComponent,
    ForgotPasswordComponent,
    InputFocusDirective,
    BadgeDirective,
    RecyleView,
    CurrentSlipComponent,
    InboxDialogComponent,
    RegisterSuccessComponent,
    FooterComponent,
    LeftSportMenuDesktop,
    SporMobileMenu,
    SideBannerSliderComponent,
    DigitOnlyDirective,
    VerticalSliderComponent,
    WithdrawResultComponent,
    TopLeagues,
    QuickTipp,
    FileUpload,
    AccountMenuComponent,
    LanguageDropdownComponent,
    LiveStreamTvComponent,
    PaymentResponseErrorSuccessComponent,
  ],
  bootstrap: [CoreComponent],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector, LocalizationService],
      multi: true,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: AritaHammerConfig,
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.recaptcha.siteKey,
      } as RecaptchaSettings,
    },
    DepositService,
    WithdrawService,
    ScrollDispatcher,
    DecimalPipe,
    PatternValidatorService,
    ConfirmPasswordService,
    CacheService,
  ],
  exports: [],
})
export class AppModule {}
